/* React hooks */
import { useEffect, useRef, useState } from "react";
/* Components */
import FormButtons from "../../components/common/FormButtons";
/* Api */
import { updateFunerariumScheduleInfos } from "../../api/pro/funerariumReq";
/* Context */
import useAuth from "../../hooks/useAuth";
/* Tools */
import { safeJwtDecode } from "../../helper/jwtHelper";
import {
    checkRequiredFields, displayMessage,
    validateFormInput
} from "../../helper/tools";
import useData from "../../hooks/useData";
import Icon from "../../components/common/Icon";


export default function FormFunerariumSchedule({ apiFunerariumGeneralInfos, legend, setShowModal }) {
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const { funerariumInfos } = useData();
    const [values, setValues] = useState();
    const [errors, setErrors] = useState([]);
    const hasLoadedInfosRef = useRef(false);

    const onChange = (e) => {
        setValues({ ...values, [e.target.name]: e.target.value })
        validateFormInput(e);
    }

    const handleTimeChange = (e) => {
        const { name } = e.target;
        let value = e.target.value;

        const numbers = value.replace(/\D/g, "");

        let formattedValue = "";
        if (numbers.length > 0) {
            const limited = numbers.slice(0, 4);

            if (limited.length <= 2) {
                formattedValue = limited;
            } else {
                formattedValue = limited.slice(0, 2) + ":" + limited.slice(2);
            }
        }

        setValues(prev => ({
            ...prev,
            [name]: formattedValue
        }));

        e.target.value = formattedValue;
        validateFormInput(e);
    }

    const handleRemoveModal = () => {
        setShowModal(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }
        setErrors([]);
        let body = {
            agencyId: agencyId,
            morningStart: values?.morningStart || null,
            morningEnd: values?.morningEnd || null,
            afternoonStart: values?.afternoonStart || null,
            afternoonEnd: values?.afternoonEnd || null,
            visitInfos: values?.visitInfos,
        }
        await updateFunerariumScheduleInfos(token, body)
            .then(response => {
                let messages = [];
                if (response?.response?.data) {
                    for (let error of response?.response?.data) {
                        messages.push({ "text": error.error, type: "error" });
                    }
                    setErrors(messages);
                } else {
                    setErrors(["ok"]);
                }
            })
    }

    useEffect(() => {
        if (errors?.length > 0) {
            for (let error of errors) {
                displayMessage(error);
            }

        }
        if (errors[0] === "ok") {
            setShowModal(false)
            apiFunerariumGeneralInfos();
        }
    }, [errors]);

    // Ne pré-remplir le formulaire qu'au tout premier chargement des vraies données (funerariumInfos
    // vaut [] tant que la requête initiale n'a pas abouti) : si cet effet se redéclenchait plus tard
    // (ex: funerariumInfos rafraîchi en arrière-plan), il écraserait silencieusement la saisie en
    // cours de l'utilisateur.
    useEffect(() => {
        if (!hasLoadedInfosRef.current && funerariumInfos && !Array.isArray(funerariumInfos)) {
            setValues(funerariumInfos);
            hasLoadedInfosRef.current = true;
        }
    }, [funerariumInfos]);

    return (
        <form className="funerarium-schedule-form" onSubmit={handleSubmit} method="POST" noValidate>
            <fieldset>
                {(legend !== "no-legend") && <Icon span={legend} source="fa fa-calendar" />}
                <p className="hours-format-hint">
                    <i className="fa fa-info-circle"></i>
                    Format des heures : HH:MM (ex: 09:00)
                </p>

                <div className="form-input schedule-period">
                    <label htmlFor="morningStart" className="schedule-period-label">
                        <i className="fa fa-sun-o"></i>
                        Matin
                    </label>
                    <div className="schedule-period-row">
                        <div className="schedule-time-group">
                            <label htmlFor="morningStart">De</label>
                            <input
                                pattern="[0-2][0-9]:[0-5][0-9]"
                                type="text"
                                name="morningStart"
                                onChange={handleTimeChange}
                                value={values?.morningStart || ""}
                                placeholder="09:00"
                                maxLength={5}
                                className="schedule-time-input"
                            />
                        </div>
                        <div className="schedule-time-group">
                            <label htmlFor="morningEnd">à</label>
                            <input
                                pattern="[0-2][0-9]:[0-5][0-9]"
                                type="text"
                                name="morningEnd"
                                onChange={handleTimeChange}
                                value={values?.morningEnd || ""}
                                placeholder="12:00"
                                maxLength={5}
                                className="schedule-time-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-input schedule-period">
                    <label htmlFor="afternoonStart" className="schedule-period-label">
                        <i className="fa fa-moon-o"></i>
                        Après-midi
                    </label>
                    <div className="schedule-period-row">
                        <div className="schedule-time-group">
                            <label htmlFor="afternoonStart">De</label>
                            <input
                                pattern="[0-2][0-9]:[0-5][0-9]"
                                type="text"
                                name="afternoonStart"
                                onChange={handleTimeChange}
                                value={values?.afternoonStart || ""}
                                placeholder="14:00"
                                maxLength={5}
                                className="schedule-time-input"
                            />
                        </div>
                        <div className="schedule-time-group">
                            <label htmlFor="afternoonEnd">à</label>
                            <input
                                pattern="[0-2][0-9]:[0-5][0-9]"
                                type="text"
                                name="afternoonEnd"
                                onChange={handleTimeChange}
                                value={values?.afternoonEnd || ""}
                                placeholder="18:00"
                                maxLength={5}
                                className="schedule-time-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-input">
                    <label htmlFor="visitInfos">
                        <i className="fa fa-info-circle"></i>
                        Informations sur les visites
                    </label>
                    <textarea
                        name="visitInfos"
                        onChange={onChange}
                        value={values?.visitInfos}
                        rows="4"
                        placeholder="Informations complémentaires sur les horaires de visite..."
                        className="schedule-visit-textarea"
                    />
                </div>
                <div className="errors"></div>
                <div className="message"></div>
            </fieldset>
            <FormButtons handleRemoveModal={handleRemoveModal} />
        </form>
    )
}